import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { createTargetURL } from '@src/quince-site-common/src/hooks/useQueryState'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'

import { getInitialSelectedOptionsForProduct } from '../productPurchase/ProductPurchase'

import { MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF } from './constant'

export const QUERY_KEYS: ('filter' | 'sortBy')[] = ['filter', 'sortBy']

export interface MiniPdpRecommendationCacheContext {
  collectionId: string
  productId: string | number
  color: string | undefined
}

const MINI_PDP_RECO_CACHE_MARKER = '|miniPdpReco='

export const getMiniPdpCollectionCacheKey = (
  pathname: string,
  searchParams: string,
  reco: MiniPdpRecommendationCacheContext,
): string => {
  const base = getCollectionCacheKey(pathname, searchParams)
  const segment = `${reco.collectionId}::${String(reco.productId)}::${reco.color}`
  return `${base}${MINI_PDP_RECO_CACHE_MARKER}${segment}`
}

export const scrollToMiniPDPCollectionSection = () => {
  if (!isBrowser()) return

  const element = document.querySelector(`[data-section-name="${MINI_PDP_COLLECTION_SECTION_NAME_FOR_REF}"]`)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

export const handleFilterSectionScroll = (filterSectionRef: React.RefObject<HTMLDivElement>) => {
  if (filterSectionRef.current && isBrowser()) {
    // Check if filterSectionRef is visible in viewport
    const filterSectionRect = filterSectionRef.current.getBoundingClientRect()
    const isFilterSectionVisible =
      filterSectionRect.top >= 0 &&
      filterSectionRect.left >= 0 &&
      filterSectionRect.bottom <= window.innerHeight &&
      filterSectionRect.right <= window.innerWidth

    // If not visible and user has scrolled past it (filterSectionRect.top is negative), scroll to it
    if (!isFilterSectionVisible && filterSectionRect.top < 0) {
      const headerHeight = document.querySelector('#site-primary-header')?.getBoundingClientRect().height ?? 0
      const SCROLL_TOP_PADDING = 16
      const top = window.pageYOffset + filterSectionRect.top - headerHeight - SCROLL_TOP_PADDING
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}

export const resetCollectionCacheOnFilterChange = ({
  pathname,
  searchParams,
  updatedWatchedQueryState,
  recommendationContext,
}: {
  pathname: string
  searchParams: string
  updatedWatchedQueryState: { filter: string; sortBy: string | undefined }
  recommendationContext: MiniPdpRecommendationCacheContext
}) => {
  if (!isBrowser()) return

  const targetURLSearchParams = createTargetURL({
    updatedWatchedQueryState,
    searchParams: new URLSearchParams(searchParams),
    queryKeys: QUERY_KEYS,
  })

  const destinationSearchParams = targetURLSearchParams.slice(1) // remove leading ?
  const cacheKey = getMiniPdpCollectionCacheKey(pathname, destinationSearchParams, recommendationContext)
  const collectionCache = collectionCacheStateManager.getCollectionCache(cacheKey)
  if (collectionCache) {
    collectionCacheStateManager.publishCollectionCache(cacheKey, 0)
  }
}

export const getSelectedColor = ({
  product,
  sectionData,
  pdpSelectedOptions,
  productIdFromRedux,
}: {
  product: Product
  sectionData: TPDPSectionData
  pdpSelectedOptions?: ProductVariantOption[]
  productIdFromRedux?: string
}) => {
  const selectedOptionsState =
    productIdFromRedux != null && productIdFromRedux === String(product.id) && pdpSelectedOptions?.length
      ? pdpSelectedOptions
      : null

  const selectedOptions = selectedOptionsState ?? getInitialSelectedOptionsForProduct(product, sectionData)

  return getSelectedColorFromSelectedOptions(selectedOptions)
}
